import React from "react";
import Link from "next/link";
import { LearnLayout } from "@/components/layout/LearnLayout";
import { Calendar, Navigation, Layers, CheckCircle, Info, ArrowRight } from "lucide-react";
import { useUniversityContent } from "@/lib/university/useUniversityContent";
import { getUniversityPack } from "@/lib/universityPack";
import { RequireYearOfStudy, RequireStudentAccess } from "@/components/auth/EntitlementGuards";

export default function YAAGGuide() {
    const content = useUniversityContent();
    const pack = getUniversityPack();
    const guideData = content.guides.yaag;

    return (
        <RequireStudentAccess>
            <RequireYearOfStudy>
                <LearnLayout
                title={guideData.seo_title}
                description={guideData.seo_desc}
                slug="year-at-a-glance"
                relatedArticles={[
                    { title: "AI Study Assistant", slug: "ai-study-assistant" },
                    { title: "Smart Chat: Legal Prompting", slug: "smart-chat-interface" },
                ]}
            >
                <div className="prose prose-blue max-w-none">
                    {/* ... content ... */}
                    <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 font-display">
                        {guideData.title}
                    </h1>

                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                        The Year at a Glance (YAAG) dashboard is the visual backbone of your academic life.
                        It maps your entire degree path, from your first Michaelmas tutorial to graduation,
                        ensuring you always know exactly where you are in the syllabus and what deadlines are
                        approaching.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12 not-prose">
                        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                            <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 mb-4">
                                <Navigation className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Navigate Your Degree</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Use the year selector to switch between Foundation, Year 1, 2, and 3.
                                Review past terms to check coverage or peek into the future to prepare for upcoming modules.
                            </p>
                        </div>
                        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-4">
                                <Layers className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Termly Drill-down</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Click on any month or specific week to open the detailed calendar view.
                                From there, you can see daily lectures, tutorials, and specific reading tasks.
                            </p>
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">How assignments flow in</h2>
                    <p>
                        YAAG isn&apos;t just a static calendar. When you select your modules, the platform automatically
                        populates your timeline with major assessment windows and formative deadlines based on
                        standard {pack.display_name} Law handbooks.
                    </p>
                    <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl my-8">
                        <h4 className="flex items-center gap-2 text-amber-900 font-bold mt-0 mb-3">
                            <Info className="w-4 h-4 text-amber-600" />
                            Academic Integrity Note
                        </h4>
                        <p className="text-amber-800 text-sm mb-0">
                            The YAAG dashboard is a planning and productivity tool. While it helps you stay ahead,
                            <strong> you must always check your official University exam timetables and VLE
                                (e.g., Canvas/DUO)</strong> for the definitive submission times and locations.
                        </p>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Pro Workflow</h2>
                    <ul className="space-y-4">
                        <li className="flex gap-4">
                            <div className="mt-1 bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</div>
                            <div>
                                <strong>Sync your calendar:</strong> Import your personal timetable from your university calendar
                                to overlay tutorials and personal commitments on top of the academic roadmap.
                            </div>
                        </li>
                        <li className="flex gap-4">
                            <div className="mt-1 bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</div>
                            <div>
                                <strong>Spot the &apos;Crunches&apos;:</strong> Identify weeks where multiple assignments
                                converge. YAAG makes these &quot;red zones&quot; visual so you can start preparations early.
                            </div>
                        </li>
                        <li className="flex gap-4">
                            <div className="mt-1 bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</div>
                            <div>
                                <strong>Track Progress:</strong> As you complete weeks, the progress bars update,
                                giving you a psychological win as you move closer to graduation.
                            </div>
                        </li>
                    </ul>

                    <div className="mt-16 flex flex-col items-center p-10 bg-gray-50 border border-gray-100 rounded-3xl text-center">
                        <Calendar className="w-12 h-12 text-gray-300 mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to plan your success?</h3>
                        <p className="text-gray-600 mb-6 max-w-sm">Return to your dashboard and start mapping your legal pathway.</p>
                        <Link href="/year-at-a-glance">
                            <button className="bg-indigo-600 text-white font-bold py-3 px-8 rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center gap-2">
                                Back to YAAG <ArrowRight className="w-4 h-4" />
                            </button>
                        </Link>
                    </div>

                    {/* Footer Policy Disclaimer */}
                    <p className="mt-20 text-[10px] text-gray-400 text-center uppercase tracking-widest">
                        {content.legal.disclaimer}
                    </p>
                </div>
            </LearnLayout>
        </RequireYearOfStudy>
        </RequireStudentAccess>
    );
}
